/**
 * NIGHTBOUND - starter selection card.
 *
 * Shows the real species data (stats, starting attack, sleep profile) and a
 * live preview of the creature art. Nothing here is computed on the client; it
 * renders the catalogue the server served.
 */

import { useMemo } from 'react'
import { getAbility } from '../../shared/abilities'
import { sleepWindowLabel, type SpeciesDefinition } from '../../shared/species'
import { creatureArtCandidates } from '../art/creatureArt'
import { ProceduralCreature } from '../art/proceduralCreatures'
import { useFirstAvailableAsset } from '../hooks'
import { cx } from './ui'

export interface SpeciesCardProps {
  species: SpeciesDefinition
  selected: boolean
  onSelect: () => void
}

const STAT_LABELS: Array<{ key: 'hp' | 'attack' | 'defence' | 'speed'; label: string }> = [
  { key: 'hp', label: 'Health' },
  { key: 'attack', label: 'Attack' },
  { key: 'defence', label: 'Defence' },
  { key: 'speed', label: 'Speed' },
]

export function SpeciesCard({ species, selected, onSelect }: SpeciesCardProps) {
  const candidates = useMemo(
    () => creatureArtCandidates(species.assetKey, 'IDLE'),
    [species.assetKey],
  )
  const { src } = useFirstAvailableAsset(candidates)
  const ability = getAbility(species.startingAbilityKey)

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={cx(
        'group relative flex w-full flex-col overflow-hidden rounded-[4px] border text-left transition-all',
        selected
          ? 'border-ember-600/70 bg-night-800/80 shadow-[0_0_30px_-14px_rgba(201,164,106,0.7)]'
          : 'border-ash-700/50 bg-night-900/70 hover:border-ash-500 hover:bg-night-800/60',
      )}
    >
      <div className="relative flex items-end justify-center overflow-hidden border-b border-ash-700/40 bg-gradient-to-b from-night-850 to-night-950 pb-2 pt-4">
        <span
          aria-hidden
          className="absolute inset-0 opacity-[0.18]"
          style={{
            background: `radial-gradient(60% 55% at 50% 70%, ${species.accent} 0%, transparent 72%)`,
          }}
        />
        <div className="relative h-28 w-32">
          {src ? (
            <img
              src={src}
              alt=""
              aria-hidden
              className="h-full w-full object-contain drop-shadow-[0_10px_12px_rgba(0,0,0,0.6)]"
            />
          ) : (
            <ProceduralCreature
              speciesId={species.id}
              accent={species.accent}
              state="IDLE"
              className="h-full w-full"
            />
          )}
        </div>
        {selected ? (
          <span className="absolute right-2 top-2 rounded-full border border-ember-600/70 bg-night-950/80 px-2 py-0.5 nb-label text-[8px] text-ember-400">
            Chosen
          </span>
        ) : null}
      </div>

      <div className="flex flex-1 flex-col gap-2 p-3">
        <div className="flex items-baseline justify-between gap-2">
          <h3 className="font-display text-lg text-bone-200">{species.name}</h3>
          <span className="nb-label text-[9px] text-ash-400">{species.family}</span>
        </div>
        <p className="text-[11px] uppercase tracking-[0.14em] text-ash-400">
          {species.role} · {species.temperament}
        </p>
        <p className="text-xs leading-relaxed text-bone-300/90">{species.shortDescription}</p>

        <dl className="mt-1 grid grid-cols-2 gap-x-3 gap-y-1">
          {STAT_LABELS.map((stat) => (
            <div key={stat.key} className="flex items-center justify-between gap-2">
              <dt className="nb-label text-[9px]">{stat.label}</dt>
              <dd className="nb-numeric text-xs text-bone-300">{species.baseStats[stat.key]}</dd>
            </div>
          ))}
        </dl>

        <div className="mt-1 space-y-1 border-t border-ash-700/40 pt-2 text-[11px] text-ash-300">
          {ability ? (
            <p>
              <span className="nb-label text-[9px] text-ash-400">Starts with</span>{' '}
              <span className="text-bone-300">{ability.name}</span>
            </p>
          ) : null}
          <p>
            <span className="nb-label text-[9px] text-ash-400">Sleeps</span>{' '}
            <span className="text-bone-300">{sleepWindowLabel(species)}</span>
          </p>
        </div>

        <p className="mt-1 font-display text-[11px] italic leading-relaxed text-ash-400">
          {species.whisper}
        </p>
      </div>
    </button>
  )
}
